"use client";

import { useState } from 'react';
import Image from 'next/image';
import PropertyTabs from './ui/property-tabs';
import PhotoGalleryModal from './ui/photo-gallery-modal';
import SimpleBookingCard from './ui/simple-booking-card';
import MobileBookingButton from './ui/mobile-booking-button';

export default function Home() {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  return (
    <div id="property-container">
        <button
            id="property-image"
            onClick={() => setIsGalleryOpen(true)}
            className="flex overflow-hidden w-full relative group"
        >
            <Image
                src="/cabin-front.jpg"
                width={4284}
                height={5712}
                alt="Silver Trail Cabin"
                className="h-64 md:h-120 w-full animate-fade-in rounded-xl object-cover shadow-lg animate-slide-up duration-300"
            />
            <div className="md:hidden absolute bottom-3 left-1/2 -translate-x-1/2 px-4 py-2 rounded-md bg-green-800 hover:bg-green-700 transition-colors text-sm font-medium">
                See all photos
            </div>
        </button>

        <div id="property-image2" className="hidden md:flex flex-col gap-3 lg:gap-5 w-full md:w-100 h-auto md:h-120">
            <button
                onClick={() => setIsGalleryOpen(true)}
                className="shrink relative isolate rounded-xl object-cover shadow-lg hover:opacity-90 transition-opacity"
                type="button"
            >
                <div className="relative h-48 md:h-60 w-full overflow-hidden">
                    <Image
                        src="/cabin-backside-1.jpg"
                        width={1600}
                        height={1600}
                        alt="Cabin view"
                        className="animate-fade-in rounded-xl object-cover shadow-lg animate-slide-up duration-300 w-full h-full"
                    />
                    <div className="absolute left-1/2 top-1/2 grid -translate-x-1/2 -translate-y-1/2 grid-cols-[auto,1fr] items-center gap-3 opacity-0 hover:opacity-100 transition-opacity">
                        <div className="btn btn-rounded h-16 w-16 lg:h-20 lg:w-20">
                            <svg width="80" height="80" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="shadow-lg transform transition duration-300 hover:scale-105">
                                <circle cx="50" cy="50" r="45" fill="black" fillOpacity="0.7"/>
                                <path d="M40 35L70 50L40 65L40 35Z" fill="white"/>
                                <circle cx="50" cy="50" r="47" stroke="#93C5FD" strokeWidth="2" opacity="0.3"/>
                            </svg>
                        </div>
                    </div>
                </div>
            </button>

            <button
                onClick={() => setIsGalleryOpen(true)}
                className="shrink relative isolate rounded-xl object-cover shadow-lg hover:opacity-90 transition-opacity"
                type="button"
            >
                <div className="relative h-48 md:h-55 w-full overflow-hidden">
                    <Image
                        src="/cabin-backside-2.jpg"
                        width={1600}
                        height={1600}
                        alt="Cabin view"
                        className="animate-fade-in rounded-xl object-cover shadow-lg animate-slide-up duration-300 w-full h-full"
                    />
                    <div className="btn btn-sm absolute bottom-3 left-1/2 w-fit -translate-x-1/2 px-3 py-2 rounded-md bg-green-800 hover:bg-green-700 transition-colors text-sm md:text-base">
                        See all photos
                    </div>
                </div>
            </button>
        </div>

        <div id="property-hero" className="flex flex-col gap-y-3 md:gap-y-5">
            <div className="text-eyebrow-xs font-medium uppercase leading-4 !tracking-[1.8px] text-w-4004 text-xs md:text-sm">Easton, Washington</div>
            <div className="flex">
                <h1 className="flex min-h-[40px] md:min-h-[52px] lg:min-h-16 w-full flex-wrap gap-x-2 md:gap-x-3 lg:gap-x-4 font-headline leading-[40px] md:leading-[52px] lg:leading-[72px] text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold items-center relative pr-3">
                    Silver Trail Cabin
                </h1>
                <div id="hero-actions" className="flex items-center">
                    <button className="flex gap-2 rounded-md pl-1 pr-1 border-1">
                        <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
                            <g className="transform transition duration-300 hover:scale-110">
                                <circle cx="70" cy="30" r="10" stroke="#FFFFFF" strokeWidth="4"/>
                                <circle cx="70" cy="70" r="10" stroke="#FFFFFF" strokeWidth="4"/>
                                <circle cx="30" cy="50" r="10" stroke="#FFFFFF" strokeWidth="4"/>
                                <line x1="60" y1="35" x2="40" y2="45" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round"/>
                                <line x1="40" y1="55" x2="60" y2="65" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round"/>
                            </g>
                        </svg>
                        <span className="hidden xl:block">Share</span>
                    </button>
                </div>
            </div>

            <div className="isolate relative block w-full overflow-hidden min-h-8">
                <div className="flex gap-3 md:gap-5 overflow-x-auto no-scrollbar text-sm md:text-base">
                    <div className="flex items-center gap-1">
                        <div className="text-nowrap">4 bedrooms</div>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="text-nowrap">3 baths</div>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="text-nowrap">6 beds</div>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="text-nowrap">12 guests</div>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="text-nowrap">2000 sqft</div>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="text-nowrap">Game Room</div>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="text-nowrap">1.5 acres</div>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="text-nowrap">Nearby Skiing</div>
                    </div>
                </div>
            </div>
        </div>

        <div id="property-tabs-container">
            <PropertyTabs />
        </div>

        <div id="property-booking" className={`pb-10 ${isGalleryOpen ? 'z-0' : 'z-100'}`}>
            <div className="sticky top-[5.5rem]">
                <SimpleBookingCard />
            </div>
        </div>

        <PhotoGalleryModal isOpen={isGalleryOpen} onClose={() => setIsGalleryOpen(false)} />

        {/* Mobile-only sticky Reserve button and modal */}
        <MobileBookingButton />
    </div>
  )
}
